import { Avatar, Button, Divider, Typography } from "@suraasa/placebo-ui"
import User from "assets/icons/system/User"
import clsx from "clsx"
import SlidingSheet from "components/SlidingSheet"
import { AuthManager } from "features/Authentication/AuthManager"
import { DEFAULT_PROFILE_COVER, getCoverImageURL } from "features/Profile/utils"
import { context } from "global/Context/context"
import SwitchGrowthspace from "growthspace/SwitchGrowthspace"
import { HeadsetHelp, LogOut, NavArrowDown, Settings } from "iconoir-react"
import { useContext, useState } from "react"
import { Link } from "react-router-dom"
import routes from "routes"
import { buildUserName, getPlatformURL } from "utils/helpers"

import styles from "./Sidebar.module.css"

const svgProps = {
  width: 20,
  height: 20,
}

type Props = {
  open: boolean
  handleClose: () => void
}

type MenuItem = {
  key?: string
  name: string
  icon: any
  endIcon?: JSX.Element
  bottomBorder?: boolean
  disabled?: boolean
} & ({ href: string } | { key: "more" | "sign_out" })

const menuItems: MenuItem[] = [
  {
    name: "Profile",
    href: routes.profile.self,
    icon: User,
  },
  {
    name: "Settings",
    href: routes.settings,
    icon: Settings,
  },
  {
    name: "Help Center",
    href: getPlatformURL("sso", "/help"),
    icon: HeadsetHelp,
  },
  {
    name: "Sign Out",
    key: "sign_out",
    icon: LogOut,
    bottomBorder: true,
  },
]

const SidebarMenuItem = ({
  item,
  handleAction,
}: {
  item: MenuItem
  handleAction: () => void
}) => {
  const display = (
    <div
      className={clsx("flex w-full grow items-center gap-1 py-2", {
        "border-b border-onSurface-200": item.bottomBorder,
        "text-onSurface-500": item.disabled,
        "text-critical-500": ("key" in item && item.key) === "sign_out",
      })}
    >
      <item.icon {...svgProps} />
      <Typography variant="subtitle2">{item.name}</Typography>
    </div>
  )
  if ("href" in item && !item.disabled) {
    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <Link to={item.href} onClick={handleAction}>
        {display}
      </Link>
    )
  }

  return (
    <button
      className="flex items-center justify-between"
      onClick={handleAction}
      disabled={item.disabled}
    >
      {display}
      {item.endIcon && item.endIcon}
    </button>
  )
}

const Sidebar = ({ open, handleClose }: Props) => {
  const { authInfo, profile } = useContext(context)

  const userName = authInfo ? buildUserName(authInfo.user) : ""
  const [showMore, setShowMore] = useState(false)

  const handleAction = (item: MenuItem) => {
    if ("key" in item) {
      switch (item.key) {
        case "more":
          setShowMore(prevState => !prevState)
          return
        case "sign_out":
          AuthManager.redirectToLogout()
          return
        default:
          throw new Error("Action not handled in profile sidebar")
      }
    }
  }

  const getEndIcon = (item: MenuItem) => {
    if ("key" in item) {
      if (item.key === "more") {
        return (
          <div
            className={clsx(styles.arrowDown, {
              [styles.arrowDownActive]: showMore,
            })}
          >
            <NavArrowDown />
          </div>
        )
      }
    }
  }

  return (
    <SlidingSheet
      mountOnBody
      className={clsx(
        "flex min-w-[320px] flex-col justify-between",
        styles.sidebarContainer
      )}
      open={open}
      onClose={handleClose}
      from="end"
    >
      <div className="flex flex-col">
        <div className="relative">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            onClick={handleClose}
            to={routes.profile.self}
            className="relative mb-[101px] block w-full px-0.5 pt-0.5"
          >
            <div
              style={{
                backgroundImage: `url(${getCoverImageURL(
                  profile.coverStyle || DEFAULT_PROFILE_COVER,
                  true
                )})`,
              }}
              className="relative h-[65px] w-full rounded bg-cover text-black"
            />
            <div className="absolute left-0 top-4.5 flex w-full flex-col items-center">
              <Avatar
                src={profile.picture}
                name={userName}
                className={clsx("mb-1 size-9")}
              />

              <Typography variant="title4">{userName}</Typography>
              <Typography variant="smallBody" className="text-onSurface-500">
                View Profile
              </Typography>
            </div>
          </Link>
        </div>
        <div className="flex flex-col px-2">
          <Divider className="bg-onSurface-200" weight="light" />

          {menuItems.map((item, index) => (
            <SidebarMenuItem
              item={{ ...item, endIcon: getEndIcon(item) }}
              key={index}
              handleAction={() => {
                if ("href" in item && !item.disabled) {
                  handleClose()
                } else {
                  handleAction(item)
                }
              }}
            />
          ))}
        </div>
        <SwitchGrowthspace />
      </div>
      <div className="flex flex-col px-2 pb-2">
        <a href={getPlatformURL("suraasa", "/privacy-policy")}>
          <Button className={clsx(styles.termsConditionsButton)} variant="text">
            <Typography variant="smallBody" className="text-onSurface-400">
              Privacy Policy
            </Typography>
          </Button>
        </a>

        <a href={getPlatformURL("suraasa", "/terms-of-use")}>
          <Button className={styles.termsConditionsButton} variant="text">
            <Typography variant="smallBody" className="text-onSurface-400">
              Terms and Conditions
            </Typography>
          </Button>
        </a>

        <Typography variant="smallBody" className="mt-0.25 text-onSurface-400">
          v{APP_VERSION}
        </Typography>
      </div>
    </SlidingSheet>
  )
}

export default Sidebar
